import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ArrowsClockwise } from '@phosphor-icons/react';
import { formatNzDateTime } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

// Human-readable labels for the event_type enum the backend exposes via
// /api/audit-logs. Keep keys in sync with backend/audit.py EVENT_TYPES.
const EVENT_LABELS = {
  password_change_success: 'Password changed',
  password_change_failure: 'Password change failed',
  password_reset: 'Admin reset password',
  prepay_bank: 'Prepay credit banked',
  prepay_apply: 'Prepay credit applied',
  prepay_clear: 'Prepay credit cleared',
  prepay_manual_adjust: 'Prepay credit manually adjusted',
  terms_accept: 'Terms of Trade accepted',
  impersonation_start: 'Customer mode started',
  impersonation_stop: 'Customer mode ended',
};

const EVENT_BADGE_CLASSES = {
  password_change_success: 'bg-success/15 text-success border-success/40',
  password_change_failure: 'bg-danger/15 text-danger border-danger/40',
  password_reset: 'bg-warning/15 text-warning border-warning/40',
  prepay_bank: 'bg-gold/15 text-gold border-gold/40',
  prepay_apply: 'bg-gold/15 text-gold border-gold/40',
  prepay_clear: 'bg-danger/15 text-danger border-danger/40',
  prepay_manual_adjust: 'bg-warning/15 text-warning border-warning/40',
  terms_accept: 'bg-success/15 text-success border-success/40',
  impersonation_start: 'bg-warning/15 text-warning border-warning/40',
  impersonation_stop: 'bg-warning/15 text-warning border-warning/40',
};

// Compact, human-friendly summary of the row-level metadata blob.
const renderMetadata = (row) => {
  const m = row.metadata || {};
  const parts = [];
  if (row.eventType === 'prepay_bank' || row.eventType === 'prepay_apply') {
    if (m.amount !== undefined) parts.push(`$${Number(m.amount).toFixed(2)}`);
    if (m.orderNumber) parts.push(`order ${m.orderNumber}`);
  }
  if (row.eventType === 'prepay_clear' || row.eventType === 'prepay_manual_adjust') {
    if (m.bankedBefore !== undefined && m.bankedAfter !== undefined) {
      parts.push(`$${Number(m.bankedBefore).toFixed(2)} → $${Number(m.bankedAfter).toFixed(2)}`);
    }
  }
  if (row.eventType === 'password_change_failure' && m.reason) {
    parts.push(m.reason.replace(/_/g, ' '));
  }
  if (row.eventType === 'password_reset' && m.source) {
    parts.push(m.source.replace(/_/g, ' '));
  }
  if (row.eventType === 'terms_accept') {
    if (m.context === 'cart_submit') parts.push('Browse & Order');
    else if (m.context === 'orders_submit') parts.push('Smart Saver');
    if (m.orderNumber) parts.push(`order ${m.orderNumber}`);
    if (m.legacy) parts.push('legacy');
  }
  return parts.length ? parts.join(' · ') : '—';
};

export const AuditLogPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventType, setEventType] = useState('');
  const [search, setSearch] = useState('');

  const fetchPage = useCallback(async (skipVal, type) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/audit-logs`, {
        params: {
          skip: skipVal,
          limit: PAGE_SIZE,
          ...(type ? { eventType: type } : {}),
        },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setSkip(skipVal);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(0, eventType); }, [fetchPage, eventType]);

  // Client-side free-text filter — handy for narrowing by customer/admin email
  // without forcing a server round-trip on every keystroke. Backend filters
  // remain authoritative for event-type and pagination.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) =>
      [row.actorEmail, row.actorName, row.targetEmail, row.targetUserId, row.eventType]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [items, search]);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="audit-log-page">
      <div className="flex items-start gap-3 mb-6">
        <ShieldCheck size={32} weight="duotone" className="text-gold mt-1" />
        <div>
          <h1 className="text-3xl font-bold font-heading text-gold">Audit Log</h1>
          <p className="text-sm text-text-secondary mt-1">
            Consolidated record of every security-sensitive event in the app: password changes,
            admin resets, prepay banked-credit lifecycle (bank / apply / clear / manual adjust),
            and Terms of Trade acceptances. Newest first.
          </p>
        </div>
      </div>

      <div
        className="bg-surface border border-border-color rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-end"
        data-testid="audit-log-filters"
      >
        <div className="flex flex-col gap-1">
          <label className="text-xs text-text-secondary" htmlFor="audit-event-type">Event type</label>
          <select
            id="audit-event-type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            data-testid="audit-event-type-select"
            className="bg-black/40 border border-border-color rounded px-3 py-2 text-sm min-w-[240px]"
          >
            <option value="">All events</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
          <label className="text-xs text-text-secondary" htmlFor="audit-search">Filter (email or user id)</label>
          <input
            id="audit-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="e.g. casey@example.com"
            data-testid="audit-search-input"
            className="bg-black/40 border border-border-color rounded px-3 py-2 text-sm"
          />
        </div>

        <button
          type="button"
          onClick={() => fetchPage(skip, eventType)}
          disabled={loading}
          data-testid="audit-refresh-button"
          className="flex items-center gap-2 px-3 py-2 border border-gold/60 text-gold text-sm rounded-lg hover:bg-gold hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ArrowsClockwise size={16} weight="bold" />
          Refresh
        </button>
      </div>

      <div className="bg-surface border border-border-color rounded-xl overflow-hidden">
        {loading && (
          <p className="text-sm text-text-secondary px-4 py-6" data-testid="audit-log-loading">
            Loading audit log…
          </p>
        )}
        {error && !loading && (
          <p className="text-sm text-danger bg-danger/10 border-t border-danger/30 px-4 py-3" data-testid="audit-log-error">
            {error}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p className="text-sm text-text-secondary px-4 py-8 text-center" data-testid="audit-log-empty">
            No events match the current filters.
          </p>
        )}
        {!loading && !error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-log-table">
              <thead className="bg-black/40 text-text-secondary">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">When (NZ)</th>
                  <th className="text-left px-3 py-2 font-semibold">Event</th>
                  <th className="text-left px-3 py-2 font-semibold">Actor</th>
                  <th className="text-left px-3 py-2 font-semibold">Target</th>
                  <th className="text-left px-3 py-2 font-semibold">Details</th>
                  <th className="text-left px-3 py-2 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr
                    key={row.id || `${row.eventType}-${row.occurredAt}-${i}`}
                    data-testid={`audit-log-row-${i}`}
                    className="border-t border-border-color hover:bg-black/20 align-top"
                  >
                    <td className="px-3 py-2 font-mono text-text-primary whitespace-nowrap">
                      {formatNzDateTime(row.occurredAt)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block border px-2 py-0.5 rounded text-[10px] uppercase tracking-wide ${EVENT_BADGE_CLASSES[row.eventType] || 'border-border-color text-text-secondary'}`}
                      >
                        {EVENT_LABELS[row.eventType] || row.eventType}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-primary">
                      {row.actorEmail || row.actorName || row.actorUserId || <span className="text-text-secondary">—</span>}
                      {row.actorRole && (
                        <span className="block text-[10px] uppercase tracking-wide text-text-secondary mt-0.5">
                          {row.actorRole}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-text-primary">
                      {row.targetEmail || row.targetUserId || <span className="text-text-secondary">—</span>}
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{renderMetadata(row)}</td>
                    <td className="px-3 py-2 font-mono text-text-secondary text-[10px] whitespace-nowrap">
                      {row.ip || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && !error && total > 0 && (
        <div className="flex items-center justify-between mt-3 text-xs text-text-secondary">
          <span data-testid="audit-log-pagination-info">
            Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fetchPage(Math.max(0, skip - PAGE_SIZE), eventType)}
              disabled={skip === 0 || loading}
              data-testid="audit-log-prev-button"
              className="px-3 py-1 border border-border-color rounded hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => fetchPage(skip + PAGE_SIZE, eventType)}
              disabled={skip + items.length >= total || loading}
              data-testid="audit-log-next-button"
              className="px-3 py-1 border border-border-color rounded hover:border-gold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogPage;
